import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ScrollToTop from '../components/ScrollToTop';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <>
      <Navbar />
      <ScrollToTop />

      <div className="pt-24 pb-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Política de Privacidade
          </h1>

          <p className="text-gray-600 mb-8">
            Última atualização: {new Date().toLocaleDateString('pt-PT', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <div className="bg-white rounded-xl shadow-soft p-8 md:p-10 space-y-8">
            {/* Introdução */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introdução</h2>
              <p className="text-gray-700 leading-relaxed">
                A <strong>2Gf - Innovation Systems, Lda</strong> (doravante "2Gf", "nós", "nosso" ou "empresa"),
                proprietária da marca <strong>Vulpes</strong>, está comprometida em proteger a privacidade e os dados
                pessoais dos utilizadores dos nossos serviços e plataforma. Esta Política de Privacidade explica como
                recolhemos, utilizamos, armazenamos e protegemos as suas informações pessoais, em conformidade com o
                Regulamento Geral de Proteção de Dados (RGPD - Regulamento (UE) 2016/679) e demais legislação aplicável.
              </p>
            </section>

            {/* Identificação do Responsável */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Identificação do Responsável pelo Tratamento</h2>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>Denominação Social:</strong> 2Gf - Innovation Systems, Lda</p>
                <p className="text-gray-700 mb-2"><strong>Marca Comercial:</strong> Vulpes</p>
                <p className="text-gray-700 mb-2"><strong>Morada:</strong> Covilhã, Portugal</p>
                <p className="text-gray-700 mb-2"><strong>Email:</strong> hello@vulpes.pt</p>
                <p className="text-gray-700"><strong>Telefone:</strong> +351 969 059 898</p>
              </div>
            </section>

            {/* Dados Recolhidos */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Dados Pessoais Recolhidos</h2>
              <p className="text-gray-700 mb-4">
                Recolhemos diferentes tipos de informações, dependendo da forma como interage connosco:
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1. Dados de Identificação e Contacto</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li>Nome completo</li>
                <li>Endereço de email</li>
                <li>Número de telefone</li>
                <li>Morada (quando aplicável)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2. Dados de Utilização da Plataforma</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li>Informações de login e autenticação</li>
                <li>Dados de atividades agrícolas registadas nos Cadernos de Campo Digitais</li>
                <li>Configurações de sensores e sistemas de rega e energia</li>
                <li>Histórico de utilização da plataforma</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.3. Dados Técnicos</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Endereço IP</li>
                <li>Tipo de navegador e dispositivo</li>
                <li>Sistema operativo</li>
                <li>Cookies e tecnologias similares (ver Política de Cookies)</li>
                <li>Dados de geolocalização (quando autorizado)</li>
              </ul>
            </section>

            {/* Finalidades */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Finalidades do Tratamento de Dados</h2>
              <p className="text-gray-700 mb-4">
                Os seus dados pessoais são tratados para as seguintes finalidades:
              </p>

              <div className="space-y-4">
                <div className="border-l-4 border-primary-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">4.1. Prestação de Serviços</h4>
                  <p className="text-gray-700">
                    Fornecer acesso e funcionalidades da plataforma Vulpes, incluindo Cadernos de Campo Digitais,
                    sistemas de rega inteligente e gestão de energia.
                  </p>
                </div>

                <div className="border-l-4 border-primary-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">4.2. Gestão de Conta</h4>
                  <p className="text-gray-700">
                    Criar, gerir e manter a sua conta de utilizador, incluindo autenticação e recuperação de senha.
                  </p>
                </div>

                <div className="border-l-4 border-primary-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">4.3. Suporte ao Cliente</h4>
                  <p className="text-gray-700">
                    Responder a pedidos de informação, questões técnicas e fornecer assistência.
                  </p>
                </div>

                <div className="border-l-4 border-primary-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">4.4. Melhoria dos Serviços</h4>
                  <p className="text-gray-700">
                    Analisar dados de utilização para melhorar a plataforma, desenvolver novas funcionalidades e
                    otimizar a experiência do utilizador.
                  </p>
                </div>

                <div className="border-l-4 border-primary-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">4.5. Comunicações</h4>
                  <p className="text-gray-700">
                    Enviar notificações importantes sobre o serviço, atualizações de segurança e informações
                    relacionadas com a sua conta.
                  </p>
                </div>

                <div className="border-l-4 border-primary-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">4.6. Cumprimento Legal</h4>
                  <p className="text-gray-700">
                    Cumprir obrigações legais, regulamentares e fiscais aplicáveis.
                  </p>
                </div>
              </div>
            </section>

            {/* Base Legal */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Base Legal do Tratamento</h2>
              <p className="text-gray-700 mb-4">
                O tratamento dos seus dados pessoais baseia-se nas seguintes fundamentações legais:
              </p>

              <ul className="list-disc pl-6 space-y-3 text-gray-700">
                <li>
                  <strong>Execução de contrato:</strong> Para prestar os serviços que contratou
                </li>
                <li>
                  <strong>Consentimento:</strong> Quando fornecido explicitamente para finalidades específicas
                  (ex: marketing, newsletters)
                </li>
                <li>
                  <strong>Interesse legítimo:</strong> Para melhorar os nossos serviços e garantir a segurança
                  da plataforma
                </li>
                <li>
                  <strong>Obrigação legal:</strong> Para cumprimento de obrigações fiscais e regulamentares
                </li>
              </ul>
            </section>

            {/* Partilha de Dados */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Partilha de Dados Pessoais</h2>
              <p className="text-gray-700 mb-4">
                A 2Gf não vende, aluga ou partilha os seus dados pessoais com terceiros para fins comerciais.
                Podemos partilhar dados apenas nas seguintes circunstâncias:
              </p>

              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  <strong>Prestadores de serviços:</strong> Empresas que nos auxiliam na operação da plataforma
                  (ex: serviços de cloud, processamento de pagamentos), sob rigorosos acordos de confidencialidade
                </li>
                <li>
                  <strong>Obrigações legais:</strong> Quando exigido por lei ou autoridades competentes
                </li>
                <li>
                  <strong>Proteção de direitos:</strong> Para proteger os direitos, propriedade ou segurança
                  da 2Gf, dos utilizadores ou do público
                </li>
              </ul>
            </section>

            {/* Segurança */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Segurança dos Dados</h2>
              <p className="text-gray-700 mb-4">
                Implementamos medidas técnicas e organizacionais adequadas para proteger os seus dados pessoais
                contra acesso não autorizado, perda, destruição ou alteração, incluindo:
              </p>

              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Encriptação de dados em trânsito e em repouso (SSL/TLS)</li>
                <li>Controlo de acessos e autenticação forte</li>
                <li>Monitorização e auditoria de sistemas</li>
                <li>Backups regulares e procedimentos de recuperação</li>
                <li>Formação de colaboradores em proteção de dados</li>
              </ul>
            </section>

            {/* Retenção */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Período de Retenção</h2>
              <p className="text-gray-700">
                Os seus dados pessoais são conservados apenas pelo período necessário para as finalidades para as
                quais foram recolhidos, ou conforme exigido por lei. Após este período, os dados são eliminados ou
                anonimizados de forma segura.
              </p>

              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li><strong>Dados de conta ativa:</strong> Durante a vigência do contrato e utilização do serviço</li>
                <li><strong>Dados de faturação:</strong> 10 anos (obrigação fiscal)</li>
                <li><strong>Dados de suporte:</strong> 3 anos após resolução</li>
                <li><strong>Dados de marketing:</strong> Até revogação do consentimento</li>
              </ul>
            </section>

            {/* Direitos */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Os Seus Direitos</h2>
              <p className="text-gray-700 mb-4">
                Nos termos do RGPD, tem os seguintes direitos relativamente aos seus dados pessoais:
              </p>

              <div className="bg-primary-50 p-6 rounded-lg space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-900">✓ Direito de Acesso</h4>
                  <p className="text-gray-700 text-sm">Obter confirmação e cópia dos dados tratados</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900">✓ Direito de Retificação</h4>
                  <p className="text-gray-700 text-sm">Corrigir dados inexatos ou incompletos</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900">✓ Direito ao Apagamento</h4>
                  <p className="text-gray-700 text-sm">Solicitar a eliminação dos seus dados (direito a ser esquecido)</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900">✓ Direito à Limitação</h4>
                  <p className="text-gray-700 text-sm">Restringir o tratamento dos seus dados</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900">✓ Direito à Portabilidade</h4>
                  <p className="text-gray-700 text-sm">Receber os seus dados em formato estruturado e portável</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900">✓ Direito de Oposição</h4>
                  <p className="text-gray-700 text-sm">Opor-se ao tratamento dos seus dados</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900">✓ Direito de Retirar Consentimento</h4>
                  <p className="text-gray-700 text-sm">Revogar consentimentos previamente dados</p>
                </div>
              </div>

              <p className="text-gray-700 mt-4">
                Para exercer qualquer destes direitos, contacte-nos através de: <strong>hello@vulpes.pt</strong>
              </p>
            </section>

            {/* Reclamações */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Direito de Reclamação</h2>
              <p className="text-gray-700 mb-4">
                Tem o direito de apresentar reclamação junto da autoridade de controlo competente:
              </p>

              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>Comissão Nacional de Proteção de Dados (CNPD)</strong></p>
                <p className="text-gray-700 mb-1">Av. D. Carlos I, 134, 1º</p>
                <p className="text-gray-700 mb-1">1200-651 Lisboa, Portugal</p>
                <p className="text-gray-700 mb-1">Tel: +351 213 928 400</p>
                <p className="text-gray-700">Email: geral@cnpd.pt</p>
                <p className="text-gray-700">Website: <a href="https://www.cnpd.pt" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">www.cnpd.pt</a></p>
              </div>
            </section>

            {/* Transferências Internacionais */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Transferências Internacionais</h2>
              <p className="text-gray-700">
                Sempre que possível, os seus dados são armazenados e processados dentro do Espaço Económico Europeu (EEE).
                Caso seja necessária uma transferência internacional, garantimos que são implementadas as salvaguardas
                adequadas previstas no RGPD, tais como cláusulas contratuais-tipo aprovadas pela Comissão Europeia.
              </p>
            </section>

            {/* Menores */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Dados de Menores</h2>
              <p className="text-gray-700">
                Os nossos serviços não se destinam a menores de 16 anos. Não recolhemos intencionalmente dados de
                menores sem o consentimento dos titulares da responsabilidade parental. Se tomarmos conhecimento de
                que recolhemos dados de um menor sem autorização, eliminaremos essa informação o mais rapidamente possível.
              </p>
            </section>

            {/* Alterações */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Alterações à Política de Privacidade</h2>
              <p className="text-gray-700">
                Reservamo-nos o direito de atualizar esta Política de Privacidade periodicamente. Quaisquer alterações
                substanciais serão comunicadas através do website ou por email. A data da última atualização encontra-se
                no topo deste documento.
              </p>
            </section>

            {/* Contacto */}
            <section className="border-t pt-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contacto</h2>
              <p className="text-gray-700 mb-4">
                Para qualquer questão relacionada com esta Política de Privacidade ou com o tratamento dos seus dados
                pessoais, contacte-nos:
              </p>

              <div className="bg-primary-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>2Gf - Innovation Systems, Lda (Vulpes)</strong></p>
                <p className="text-gray-700 mb-2">Email: hello@vulpes.pt</p>
                <p className="text-gray-700 mb-2">Telefone: +351 969 059 898</p>
                <p className="text-gray-700">Morada: Covilhã, Portugal</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default PrivacyPolicyPage;
